import { useCallback, useMemo } from "react";
import { Pressable, ScrollView } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { DataStateView } from "@src/components/screen-state";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useTenantId } from "@src/features/tenants";
import { useTrafficFlowScenarios, useAssetLocations } from "../hooks";
import { buildSiteScenarios } from "../utils/config-scenarios";
import { ConfigMapPanel } from "../components/ConfigMapPanel.web";
import { ConfigScenarioList } from "../components/ConfigScenarioList.web";
import { ConfigDataSection } from "../components/ConfigDataSection.web";
import { TrafficFlowReportActions } from "../components/TrafficFlowReportActions.web";
import type { AssetMarker } from "../components/MapCanvas.web";

/**
 * Config / map page (design doc §4b). Reached from the list page's row actions
 * with a `site` (always) and optionally a `scenario` in the URL. This is where
 * the expensive, live asset-location enquiry happens, scoped to the one site.
 *
 * Three sections (§4b): map + input form, the site's scenario list (free
 * URL-driven switching), and a collapsed/lazy scenario-data section.
 */
export default function TrafficFlowConfigureScreen() {
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const router = useRouter();
  const { site: siteParam, scenario: scenarioParam } = useLocalSearchParams<{ site?: string; scenario?: string }>();
  const siteId = typeof siteParam === "string" ? siteParam : "";
  const selectedScenarioUuid = typeof scenarioParam === "string" ? scenarioParam : null;

  const {
    data: scenarios,
    isLoading: isScenariosLoading,
    isFetching: isScenariosFetching,
    error: scenariosError,
    refetch: refetchScenarios,
    isTenantPending,
  } = useTrafficFlowScenarios();
  const { sites } = useSites();

  const siteScenarios = useMemo(
    () => (scenarios ? buildSiteScenarios(scenarios, siteId) : undefined),
    [scenarios, siteId],
  );

  const siteMeta = useMemo(() => {
    const meta = sites?.find((s) => String(s.siteId) === siteId);
    return { name: meta?.siteName ?? siteId, address: meta?.address ?? undefined };
  }, [sites, siteId]);

  // Live geolocation for every asset in the site — fetched on load so the map
  // can centre and drop asset markers immediately (design doc §4b). `null` is an
  // expected result (asset couldn't be located) and simply yields no marker.
  const assetIds = useMemo(() => (siteScenarios?.assetIds ?? []).map(String), [siteScenarios]);
  const { byAssetId } = useAssetLocations(assetIds);

  // Asset context markers: only the assets we actually located.
  const assetMarkers = useMemo<AssetMarker[]>(() => {
    const markers: AssetMarker[] = [];
    for (const scenario of siteScenarios?.scenarios ?? []) {
      const id = String(scenario.assetId);
      if (markers.some((m) => m.id === id)) continue;
      const loc = byAssetId[id]?.location;
      if (loc)
        markers.push({ id, label: scenario.assetCameraLabel, coordinate: { lat: loc.latitude, lng: loc.longitude } });
    }
    return markers;
  }, [siteScenarios, byAssetId]);

  // Centring chain (design doc §4b):
  //   1. If a scenario is selected AND its asset is located → tighten to that
  //      asset (precise pin placement).
  //   2. Else fit all located asset markers.
  //   3. Else fit configured scenario pins.
  //   4. Else empty → MapCanvas falls back to the AU/NZ default view.
  const focusCoordinates = useMemo(() => {
    const selected = siteScenarios?.scenarios.find((s) => s.scenarioUuid === selectedScenarioUuid);
    if (selected) {
      const loc = byAssetId[String(selected.assetId)]?.location;
      if (loc) return [{ lat: loc.latitude, lng: loc.longitude }];
    }
    if (assetMarkers.length > 0) return assetMarkers.map((m) => m.coordinate);
    const pins = (siteScenarios?.scenarios ?? [])
      .map((s) => s.coordinate)
      .filter((c): c is { lat: number; lng: number } => !!c);
    return pins;
  }, [siteScenarios, selectedScenarioUuid, byAssetId, assetMarkers]);

  const selectedForData = useMemo(() => {
    const selected = siteScenarios?.scenarios.find((s) => s.scenarioUuid === selectedScenarioUuid);
    if (!selected) return null;
    return {
      siteId: selected.siteId,
      assetCameraKey: selected.assetCameraKey,
      scenarioUuid: selected.scenarioUuid,
      scenarioName: selected.scenarioName,
    };
  }, [siteScenarios, selectedScenarioUuid]);

  const onSelectScenario = useCallback(
    (scenarioUuid: string) => {
      const params = new URLSearchParams({ site: siteId, scenario: scenarioUuid });
      router.replace(`/tenants/${tenantId}/traffic-flow/configure?${params.toString()}` as never);
    },
    [router, tenantId, siteId],
  );

  const onBack = useCallback(() => {
    router.push(`/tenants/${tenantId}/traffic-flow` as never);
  }, [router, tenantId]);

  return (
    <WebLayout>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingTop: 16, paddingHorizontal: 24, paddingBottom: 32, gap: 16 }}
      >
        <Pressable onPress={onBack} style={{ flexDirection: "row", alignItems: "center", gap: theme.spacing.xs }}>
          <Icon name="chevronLeft" size={theme.spacing["4xl"]} color={theme.colors.icon} />
          <Text textStyle="sm/regular" color="textMuted">
            Back to worklist
          </Text>
        </Pressable>

        <View style={{ gap: theme.spacing.xxs }}>
          <Text textStyle="2xl" fontWeight="700" color="text" numberOfLines={2}>
            {siteMeta.name}
          </Text>
          {siteMeta.address ?
            <Text textStyle="sm/regular" color="textMuted" numberOfLines={2}>
              {siteMeta.address}
            </Text>
          : null}
        </View>

        {siteId ?
          <TrafficFlowReportActions tenantId={tenantId} siteId={siteId} />
        : null}

        <DataStateView
          data={siteScenarios}
          isLoading={isTenantPending || isScenariosLoading}
          isFetching={isScenariosFetching}
          error={scenariosError ?? null}
          refetch={() => {
            void refetchScenarios();
          }}
          isEmpty={(s) => s.scenarios.length === 0}
          loadingMessage="Loading site…"
          errorTitle="Unable to load scenarios"
          emptyState={{
            icon: "motion",
            title: "No scenarios for this site",
            message: "This site has no crossline mapping scenarios, or the site could not be found in this tenant.",
          }}
        >
          {(data) => (
            <View style={{ gap: theme.spacing.xl }}>
              <ConfigMapPanel
                tenantId={tenantId}
                scenarios={data.scenarios}
                assetMarkers={assetMarkers}
                focusCoordinates={focusCoordinates}
                selectedScenarioUuid={selectedScenarioUuid}
                onSelectScenario={onSelectScenario}
              />
              <ConfigScenarioList
                scenarios={data.scenarios}
                selectedScenarioUuid={selectedScenarioUuid}
                onSelect={onSelectScenario}
              />
              <ConfigDataSection selected={selectedForData} />
            </View>
          )}
        </DataStateView>
      </ScrollView>
    </WebLayout>
  );
}
